import api from './client'
import type {
  CriterionItem, GenProcess, PersistResponse, PreviewResponse,
  RequiredLookupTable, TxnCatalogEntry, ExternalConnection,
  ConnectionTypeInfo, FileSourceColumn,
} from '@/pages/designers/genprocess/types'

// ── design JSON (designers router) ─────────────────────────────────────────

export async function loadProcessObject(project: string, objName: string) {
  const res = await api.get('/designers/load-process-object', {
    params: { project, obj_name: objName },
  })
  return res.data as { success: boolean; content: unknown | null; message?: string }
}

export async function saveProcessObject(
  project: string, objName: string, data: GenProcess,
) {
  const res = await api.post('/designers/save-process-object', data, {
    headers: { 'X-Project': project, 'X-Object-Name': objName },
  })
  return res.data as { success: boolean; message?: string }
}

/** Card-list summary of a project's General Processes (Analytics › Processes). */
export interface ProjectProcessSummary {
  obj_name: string
  filename: string
  name: string
  description: string
  persist_mode: string
  /** Target table (table mode) or transaction obj_name (transaction mode). */
  target: string
  calculation_count: number
}

export async function listProjectProcesses(project: string) {
  const res = await api.get('/designers/list-project-processes', {
    params: { project },
  })
  return res.data as { success: boolean; processes: ProjectProcessSummary[] }
}

// ── runtime (genprocess router) ────────────────────────────────────────────

export interface RunArgs {
  project: string
  objName?: string
  process?: GenProcess
  params?: Record<string, unknown>
  /** File-source processes: the imported rows, keyed by declared column name
      (the header mapping is applied client-side before sending). These are the
      process INPUT — calculations still run over them — unlike `rows`, which
      are finished output rows written verbatim. */
  inputRows?: Record<string, unknown>[]
}

export async function previewProcess(
  args: RunArgs & { limit?: number; withTraces?: boolean },
) {
  const res = await api.post('/genprocess/preview', {
    project: args.project,
    obj_name: args.objName,
    process: args.process,
    params: args.params || {},
    limit: args.limit,
    with_traces: !!args.withTraces,
    input_rows: args.inputRows,
  })
  return res.data as PreviewResponse
}

export async function persistProcess(
  args: RunArgs & { rows?: Record<string, unknown>[]; ignoreWarnings?: string[] },
) {
  const res = await api.post('/genprocess/persist', {
    project: args.project,
    obj_name: args.objName,
    process: args.process,
    params: args.params || {},
    rows: args.rows,
    ignore_warnings: args.ignoreWarnings,
    input_rows: args.inputRows,
  })
  return res.data as PersistResponse
}

export async function recalcProcessRow(
  args: RunArgs & { row: Record<string, unknown>; editedColumn: string },
) {
  const res = await api.post('/genprocess/recalc', {
    project: args.project,
    obj_name: args.objName,
    process: args.process,
    params: args.params || {},
    row: args.row,
    edited_column: args.editedColumn,
  })
  return res.data as {
    row: Record<string, unknown>
    protect?: Record<string, 0 | 1>
    warning?: string | null
  }
}

// ── validation framework (run on Execute / Validate button) ─────────────────

export interface ValidationIssue {
  /** 0-based result-grid row, or null for a whole-run issue — process-level
      business logic sees the entire row set, so it may report about the run
      rather than about one row. */
  row: number | null
  field: string
  column: string
  message: string
  code?: string | null
  severity: 'E' | 'W' | 'P'
  rule?: string | null
}

export interface ValidationReport {
  ok: boolean
  errors: ValidationIssue[]
  warnings: ValidationIssue[]
  prompts: ValidationIssue[]
  row_count: number
}

/** Validate the result rows WITHOUT writing. */
export async function validateProcess(
  args: RunArgs & { rows?: Record<string, unknown>[]; ignoreWarnings?: string[] },
) {
  const res = await api.post('/genprocess/validate', {
    project: args.project,
    obj_name: args.objName,
    process: args.process,
    params: args.params || {},
    rows: args.rows,
    ignore_warnings: args.ignoreWarnings,
    input_rows: args.inputRows,
  })
  return res.data as ValidationReport
}

/** Compile/deploy this G object's item_change / validation business-logic functions. */
export async function deployProcessLogic(project: string, objName: string) {
  const res = await api.post('/genprocess/deploy-logic', {
    project, obj_name: objName,
  })
  return res.data as { results: { name?: string; status?: string; error_message?: string }[] }
}

export async function fetchProcessCriteria(
  project: string,
  opts: { process?: GenProcess; objName?: string; sourceSql?: string },
) {
  const res = await api.post('/genprocess/criteria', {
    project,
    obj_name: opts.objName,
    process: opts.process,
    source_sql: opts.sourceSql,
  })
  return res.data as { items: CriterionItem[] }
}

export async function fetchLookupsRequired(args: RunArgs) {
  const res = await api.post('/genprocess/lookups-required', {
    project: args.project,
    obj_name: args.objName,
    process: args.process,
    params: args.params || {},
  })
  return res.data as { tables: RequiredLookupTable[] }
}

/** The line forms that pull their lines from this process (line sources). */
export interface LineSourceUse {
  obj_name: string
  form_no: string
  form_title: string
  source?: string
  source_label?: string
}

export async function fetchLineSourceUsage(project: string, objName: string) {
  const res = await api.get('/genprocess/line-source-usage', {
    params: { project, obj_name: objName },
  })
  return (res.data?.used_by ?? []) as LineSourceUse[]
}

export async function fetchProcessTransactions(project: string) {
  const res = await api.get('/genprocess/transactions', { params: { project } })
  return res.data as { transactions: TxnCatalogEntry[] }
}

/** External DB connections of the project (source / target option). */
export async function fetchProjectConnections(project: string) {
  const res = await api.get('/connections/list', { params: { project } })
  return (res.data?.connections ?? []) as ExternalConnection[]
}

/** The offered database types, each carrying its `writeback` capability —
    what the Target tab needs to know whether a chosen connection can be
    written to and in which storage formats. */
export async function fetchConnectionTypes() {
  const res = await api.get('/connections/types')
  return (res.data?.types ?? []) as ConnectionTypeInfo[]
}

/** Suggest a file-source column declaration + header mapping from a parsed
    sample. Design-time only — nothing is stored until the design is saved. */
export async function inferFileColumns(args: {
  project: string
  headers: string[]
  rows: unknown[][]
  columns?: FileSourceColumn[]
}) {
  const res = await api.post('/genprocess/infer-file-columns', {
    project: args.project,
    headers: args.headers,
    rows: args.rows,
    columns: args.columns,
  })
  return res.data as {
    columns: FileSourceColumn[]
    mapping: Record<string, string>
    unmapped_headers: string[]
    sample_row_count: number
  }
}

// ── inline lookups (column + criterion search) ─────────────────────────────

/** Search a column's / criterion's reference table for the inline picker.
    Provide exactly one of `column` (target column) or `criterion`. */
export async function searchLookup(args: {
  project: string
  process?: GenProcess
  objName?: string
  column?: string
  criterion?: string
  search?: string
  limit?: number
  row?: Record<string, unknown>
}) {
  const res = await api.post('/genprocess/lookup', {
    project: args.project,
    obj_name: args.objName,
    process: args.process,
    column: args.column,
    criterion: args.criterion,
    search: args.search,
    limit: args.limit,
    row: args.row || {},
  })
  return res.data as {
    rows: { display: string; value: unknown }[]
    table: string
    value_column: string
  }
}

// ── lookup store (GENLOOKUP on the project DB) ─────────────────────────────

export async function listLookups(project: string) {
  const res = await api.get('/genprocess/lookups', { params: { project } })
  return (res.data?.lookups ?? []) as Record<string, unknown>[]
}

export async function getLookup(project: string, name: string) {
  const res = await api.get(`/genprocess/lookups/${encodeURIComponent(name)}`, {
    params: { project },
  })
  return res.data as Record<string, unknown>
}

export async function saveLookup(project: string, def: Record<string, unknown>) {
  const res = await api.post('/genprocess/lookups', { project, lookup: def })
  return res.data as Record<string, unknown>
}

export async function deleteLookup(project: string, name: string) {
  await api.delete(`/genprocess/lookups/${encodeURIComponent(name)}`, {
    params: { project },
  })
}
